import { useEffect, useMemo, useRef, useState } from 'react'
import ClockCircleOutlined from '@ant-design/icons/ClockCircleOutlined'
import Progress from 'antd/lib/progress'
import { formatMmSs } from 'Utils/common.utils'
import {
    clearProctoringTimerSnapshot,
    writeProctoringTimerSnapshot,
} from 'Utils/proctoringTimer.utils'
import styles from './ProctoringCountdownTimer.module.less'

export const ProctoringCountdownTimer = ({
    durationSeconds,
    initialRemainingSeconds,
    storageReferenceId,
    onComplete,
    active,
}: {
    durationSeconds: number
    initialRemainingSeconds?: number
    storageReferenceId?: string
    onComplete?: () => void
    active: boolean
}) => {
    const [remaining, setRemaining] = useState(() => initialRemainingSeconds ?? durationSeconds)
    const completedRef = useRef(false)

    useEffect(() => {
        setRemaining(initialRemainingSeconds ?? durationSeconds)
        completedRef.current = false
    }, [durationSeconds, initialRemainingSeconds])

    useEffect(() => {
        if (!active) return undefined

        const id = window.setInterval(() => {
            setRemaining((prev: number) => {
                if (prev <= 1) {
                    window.clearInterval(id)
                    return 0
                }
                return prev - 1
            })
        }, 1000)

        return () => window.clearInterval(id)
    }, [active, durationSeconds])

    useEffect(() => {
        if (!storageReferenceId || !active) return
        writeProctoringTimerSnapshot(storageReferenceId, remaining)
    }, [remaining, active, storageReferenceId])

    useEffect(() => {
        if (!active || remaining > 0 || completedRef.current) return
        completedRef.current = true
        if (storageReferenceId) {
            clearProctoringTimerSnapshot(storageReferenceId)
        }
        onComplete?.()
    }, [active, remaining, onComplete, storageReferenceId])

    const percent = useMemo(() => {
        if (!durationSeconds) return 0
        return Math.min(100, Math.max(0, (remaining / durationSeconds) * 100))
    }, [durationSeconds, remaining])

    return (
        <div className={styles.badge} data-testid="proctoring-countdown-timer">
            <span className={styles.ellipseWrap} aria-hidden>
                <Progress
                    type="circle"
                    percent={percent}
                    size={24}
                    strokeWidth={14}
                    status="normal"
                    format={() => <ClockCircleOutlined />}
                />
            </span>
            <span className={styles.time}>{formatMmSs(remaining)}</span>
        </div>
    )
}
